import '../../platform.css';
import '../../components/documents/documents.css';
import './archive.css';

import { useRecoilValue } from 'recoil';
import { useEffect, useMemo, useState } from 'react';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { playersState } from '../../recoil/atoms/playersState';
import { playerOverviewsState } from '../../recoil/atoms/playerOverviewsState';
import { navigationPropsState } from '../../recoil/atoms/navigationPropsState';
import { reportFiltersState } from '../../recoil/atoms/reportFiltersState';
import { useSetNavigationProps } from '../../recoil/hooks/useSetNavigationProps';
import { useGetAndAddPlayerOverview } from '../../recoil/hooks/useGetAndAddPlayerOverview';
import { getNewPlayerOverviewsForArchivedTeams, getPlayerOverviews } from '../../services/server/application/playerOverviews';
import { useOpenGlobalModal } from '../../recoil/hooks/useOpenGlobalModal';
import { useAuthContext } from '../../../common/contexts/useAuthContext';

import FolderSharedIcon from '@mui/icons-material/FolderShared';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';

import Fuse from 'fuse.js';

import { getAllReports } from '../../services/firestore/reports';
import { ViewContainerBackgroundSvg } from '../../../assets/svg/ViewContainerBackgroundSvg';
import { Report } from '../../components/documents/report/Report';
import { TeamView } from '../../components/teamView/TeamView';
import { ReportFilters } from './ReportFilters';
import { ReportStatistics } from './ReportStatistics';
import { ExistingArchivedTeams } from '../../components/documents/existingDocuments/ExistingArchivedTeams';
import { ExistingReports } from '../../components/documents/existingDocuments/ExistingReports';
import { FixedTabs } from '../../components/controls/navigation/FixedTabs';
import { getAllArchivedTeams, updateArchivedTeamIsDeletedStatus } from '../../services/firestore/archivedTeams';
import { PlayerId, PlayerOverview, PartialPlayerOverviews, PlayerOverviews } from '../../types/player';
import { PlayerReport } from '../../types/report';
import { ArchivedTeam } from '../../types/team';
import { StringToElementMap } from '../../types/common';
import { getAge } from '../../utils/ageUtils';
import { DataPosition } from '../../types/position';


export const Archive = () => {

  const { currentUser } = useAuthContext();
  const userConfig = useRecoilValue(userConfigState);

  const { openConfirmModal } = useOpenGlobalModal();

  const players = useRecoilValue(playersState);
  const playerOverviews = useRecoilValue(playerOverviewsState);
  const getAndAddPlayerOverview = useGetAndAddPlayerOverview();

  const [additionalPlayerLightOverviews, setAdditionalPlayerLightOverviews] = useState<PartialPlayerOverviews | undefined>(undefined);
  const [additionalPlayerOverviews, setAdditionalPlayerOverviews] = useState<PlayerOverviews>({});

  const reportFilters = useRecoilValue(reportFiltersState);
  const [currentReportFilterExpanded, setCurrentReportFilterExpanded] = useState<string | undefined>(undefined);

  const [allReports, setAllReports] = useState<PlayerReport[] | undefined>(undefined);
  const [reportsToShow, setReportsToShow] = useState<PlayerReport[] | undefined>(undefined);
  const [selectedReport, setSelectedReport] = useState<PlayerReport | undefined>(undefined);

  const [archivedTeams, setArchivedTeams] = useState<ArchivedTeam[] | undefined>(undefined);
  const [selectedArchivedTeam, setSelectedArchivedTeam] = useState<ArchivedTeam | undefined>(undefined);

  const [selectedPlayerOverview, setSelectedPlayerOverview] = useState<PlayerOverview | undefined>(undefined);
  const [isLoadingSelectedPlayerOverview, setIsLoadingSelectedPlayerOverview] = useState<boolean>(false);

  const hasAccessToReports = userConfig?.accessConfig.reportAccess !== 'none';
  const tabOptions = useMemo(() => hasAccessToReports ? ['reports', 'archivedTeams'] : ['archivedTeams'], [hasAccessToReports]);
  const tabOptionToTabIcon: StringToElementMap = {
    'reports': <FolderSharedIcon key={'folder-shared-icon'} style={{ fontSize: 21, marginTop: 1 }} />,
    'archivedTeams': <FolderSpecialIcon key={'folder-special-icon'} style={{ fontSize: 21, marginTop: 1 }} />
  };

  const navigationProps = useRecoilValue(navigationPropsState);
  const activeSubTab = navigationProps.activeArchiveSubTab ?? 'reports';
  const activeSubTabIndex = useMemo(() => tabOptions.indexOf(activeSubTab), [activeSubTab, tabOptions]);
  const updateNavigationProps = useSetNavigationProps();
  const setActiveSubTab = (subTab: number) => {
    updateNavigationProps({ activeArchiveSubTab: tabOptions[subTab] }, true);
  };


  const handleSetSelectedPlayerOverview = async (playerId: PlayerId) => {

    setSelectedPlayerOverview(undefined);

    const numberId = Number(playerId);

    if (isNaN(numberId)) {
      return;
    }

    if (playerOverviews[numberId]) {
      setSelectedPlayerOverview(playerOverviews[numberId]);
    }
    else if (additionalPlayerOverviews[numberId]) {
      setSelectedPlayerOverview(additionalPlayerOverviews[numberId]);
    }
    else {
      setIsLoadingSelectedPlayerOverview(true);
      const playerOverview = await getAndAddPlayerOverview(numberId, currentUser);
      setSelectedPlayerOverview(playerOverview);
      setIsLoadingSelectedPlayerOverview(false);
    }
  };


  const openConfirmLeaveReportModal = (action: () => void) => {
    openConfirmModal(
      action,
      'exitReport?',
      'exitReportInfo'
    );
  };


  const handleSetActiveSubTab = (subTab: number) => {
    setActiveSubTab(subTab);
    setSelectedReport(undefined);
    setSelectedArchivedTeam(undefined);
    setSelectedPlayerOverview(undefined);
  };

  const handleSubTabClick = (subTab: number) => {
    if (navigationProps.reportHasUnsavedChanges) {
      openConfirmLeaveReportModal(() => handleSetActiveSubTab(subTab));
    }
    else {
      handleSetActiveSubTab(subTab);
    }
  };


  const handleCloseReport = () => {
    setSelectedReport(undefined);
    setSelectedPlayerOverview(undefined);
    updateNavigationProps({ reportHasUnsavedChanges: false });
  };

  const handleReportCloseIconClick = () => {
    if (navigationProps.reportHasUnsavedChanges) {
      openConfirmLeaveReportModal(() => handleCloseReport());
    }
    else {
      handleCloseReport();
    }
  };


  const handleSelectReport = (report: PlayerReport | undefined) => {
    setSelectedReport(report);
    updateNavigationProps({ reportHasUnsavedChanges: false });
    if (report) {
      handleSetSelectedPlayerOverview(report.playerId);
    }
  };

  const handleReportClicked = (report: PlayerReport | undefined) => {
    if (navigationProps.reportHasUnsavedChanges) {
      openConfirmLeaveReportModal(() => handleSelectReport(report));
    }
    else {
      handleSelectReport(report);
    }
  };


  const updateReport = (report: PlayerReport) => {
    if (!allReports) return;

    let reportWasDeleted = false;

    const updatedReports = allReports.map((existingReport: PlayerReport) => {
      if (existingReport.id === report.id) {

        if (!existingReport.isDeleted && report.isDeleted) {
          reportWasDeleted = true;
        }

        return report;
      }

      return existingReport;
    });

    setAllReports(updatedReports);
    setSelectedReport(reportWasDeleted ? undefined : report);
    updateNavigationProps({ reportHasUnsavedChanges: false });
  };


  const deleteOrRestoreArchivedTeam = async () => {
    if (!userConfig || !selectedArchivedTeam?.id || !archivedTeams) return;

    const updatedArchivedTeam = await updateArchivedTeamIsDeletedStatus(
      selectedArchivedTeam.id,
      !selectedArchivedTeam.isDeleted,
      userConfig.club,
      currentUser
    );

    if (updatedArchivedTeam) {
      setArchivedTeams(archivedTeams.map(team => {
        if (team.id === selectedArchivedTeam.id) {
          team.isDeleted = !team.isDeleted;
        }
        return team;
      }));
    }

    if (selectedArchivedTeam.isDeleted) {
      setSelectedArchivedTeam(undefined);
    }
  };


  // load reports and archived teams only when the tab is active
  useEffect(() => {
    if (userConfig) {
      if (activeSubTab === 'reports' && !allReports) {
        getAllReports(userConfig, currentUser).then((allReports: PlayerReport[]) => {
          setAllReports(allReports);
        });
      }

      if (activeSubTab === 'archivedTeams' && !archivedTeams) {
        getAllArchivedTeams(userConfig, currentUser).then((allArchivedTeams: ArchivedTeam[]) => {
          setArchivedTeams(allArchivedTeams);
        });
      }
    }
  }, [activeSubTab, currentUser, userConfig]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (allReports && allReports.length > 0 && playerOverviews && players) {
      // get id of all players
      const playerIdsToFetch = new Set<number>();

      allReports.forEach(report => {
        const numberId = Number(report.playerId);
        if (!isNaN(numberId) && !playerOverviews[numberId] && !additionalPlayerOverviews[numberId]) {
          playerIdsToFetch.add(numberId);
        }
      });

      if (playerIdsToFetch.size === 0) {
        setAdditionalPlayerLightOverviews({});
      }
      else {
        // const include: (keyof PlayerOverview)[] = ['age', 'position_info', 'club', 'country_code'];
        getPlayerOverviews(
          Array.from(playerIdsToFetch),
          currentUser,
          // include
        ).then(partialPlayerOverviews => {
          setAdditionalPlayerLightOverviews(partialPlayerOverviews ?? {});
        });
      }
    }
  }, [allReports, currentUser, playerOverviews, players]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (archivedTeams && archivedTeams.length > 0 && playerOverviews) {
      getNewPlayerOverviewsForArchivedTeams(archivedTeams, playerOverviews, currentUser).then((newPlayerOverviews: PlayerOverviews) => {
        setAdditionalPlayerOverviews(newPlayerOverviews);
      });
    }
  }, [archivedTeams, currentUser, playerOverviews]);


  useEffect(() => {
    if (allReports) {

      // check if any of the selected filters require player data
      const filtersRequirePlayerData = reportFilters.selectedMaxAge.length > 0
        || reportFilters.selectedPositions.length > 0
        || reportFilters.selectedLeagues.length > 0
        || reportFilters.selectedNationalities.length > 0;

      if (!filtersRequirePlayerData || (additionalPlayerLightOverviews && players && playerOverviews)) {

        let filteredReports = allReports.filter(report => {

          const reportMatchesReportTypeFilter = reportFilters.selectedReportTypes.length === 0 || reportFilters.selectedReportTypes.includes(report.reportType);
          if (!reportMatchesReportTypeFilter) return false;

          const reportMatchesUserFilter = reportFilters.selectedUsers.length === 0 || reportFilters.selectedUsers.includes(report.userEmail);
          if (!reportMatchesUserFilter) return false;

          const reportMatchesDeletedFilter = navigationProps.showDeletedReports || !report.isDeleted;
          if (!reportMatchesDeletedFilter) return false;

          if (filtersRequirePlayerData && additionalPlayerLightOverviews && players && playerOverviews) {

            const numberId = Number(report.playerId);
            const playerDocument = players[report.playerId];
            const playerData = isNaN(numberId)
              ? undefined
              : additionalPlayerLightOverviews?.[numberId] ?? playerOverviews[numberId] ?? additionalPlayerOverviews[numberId];

            if (reportFilters.selectedMaxAge.length > 0) {
              const age = playerDocument?.birth_date ? getAge(playerDocument?.birth_date) : playerData?.age;
              if (!age || age >= Number(reportFilters.selectedMaxAge[0])) return false;
            }

            if (reportFilters.selectedPositions.length > 0) {
              const primaryPositions = playerDocument?.position ? [playerDocument.position] : playerData?.position_info?.primary_positions;
              if (!primaryPositions || !primaryPositions.some(position => reportFilters.selectedPositions.includes(position as DataPosition))) return false;
            }

            if (reportFilters.selectedLeagues.length > 0) {
              const competitionId = playerData?.club?.current_competition_id;
              if (competitionId === undefined || !reportFilters.selectedLeagues.includes(competitionId.toString())) return false;
            }

            if (reportFilters.selectedNationalities.length > 0) {
              const countryCode = playerDocument?.country_code ?? playerData?.country_code;
              if (!countryCode || !reportFilters.selectedNationalities.includes(countryCode)) return false;
            }
          }

          if (reportFilters.selectedRatingRequirements.length > 0) {
            // the report must match all common requirements, and one of the version specific requirements (if any are selected)
            let reportMatchesCommonRequirements = true;
            let anyVersionSpecificRequirementsSelected = false;
            let reportMatchesAnyVersionSpecificRequirements = false;

            reportFilters.selectedRatingRequirements.forEach(requirement => {

              // check if the report matches all common requirements
              if (!requirement.reportVersion && requirement.reportScores) {
                requirement.reportScores.forEach(score => {
                  const reportScore = report.reportFields[score.scoreKey] ?? 0;
                  const threshold = score.scoreValue;
                  if (reportScore < threshold) {
                    reportMatchesCommonRequirements = false;
                    return;
                  }
                });
              }
              else {
                anyVersionSpecificRequirementsSelected = true;

                // check if the report matches this version specific requirement
                if (!reportMatchesAnyVersionSpecificRequirements && requirement.reportVersion === report.reportVersion) {

                  // if no scores are provided, the report matches the requirement
                  if (!requirement.reportScores) {
                    reportMatchesAnyVersionSpecificRequirements = true;
                  }
                  else {
                    reportMatchesAnyVersionSpecificRequirements = requirement.reportScores.every(score => {
                      const reportScore = report.reportFields[score.scoreKey] ?? 0;
                      const threshold = score.scoreValue;
                      return reportScore >= threshold;
                    });
                  }
                }
              }
            });

            if (!reportMatchesCommonRequirements || (anyVersionSpecificRequirementsSelected && !reportMatchesAnyVersionSpecificRequirements)) return false;
          }

          return true;
        });

        // filter by name last
        if (reportFilters.nameSearchString) {
          const fuse = new Fuse(filteredReports, { keys: ['playerName'], threshold: 0.2, shouldSort: false });
          filteredReports = fuse.search(reportFilters.nameSearchString).map(result => result.item);
        }

        if (reportFilters.selectedOrderBy.length > 0) {
          const orderBy = reportFilters.selectedOrderBy[0];
          if (orderBy !== 'date') {
            filteredReports.sort((a, b) => {
              const aScore = a.reportFields[orderBy] ?? 0;
              const bScore = b.reportFields[orderBy] ?? 0;
              return bScore - aScore;
            });
          }
        }

        setReportsToShow(filteredReports);
      }
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [
    allReports,
    reportFilters,
    navigationProps.showDeletedReports,
    additionalPlayerLightOverviews,
    playerOverviews,
    players,
  ]);


  return (
    <div className='platform-view-section'>

      {!selectedArchivedTeam && (
        <div className='full-size'>
          <ViewContainerBackgroundSvg />
        </div>
      )}

      {currentReportFilterExpanded && <div className='filter-section-empty-background' onClick={() => setCurrentReportFilterExpanded(undefined)} />}

      <FixedTabs
        tabOptions={tabOptions}
        activeTab={activeSubTabIndex}
        setActiveTab={handleSubTabClick}
        tabIcons={tabOptions.map(tabOption => tabOptionToTabIcon[tabOption])}
        parentTab={'archive'}
      />

      <div className='platform-view-content-container-with-tabs'>

        {activeSubTab === 'reports' && (
          <div className='full-size-container'>
            {allReports && reportsToShow && (
              <div className='archive-existing-reports-container fade-in'>
                <ExistingReports
                  reports={reportsToShow}
                  selectedReport={selectedReport}
                  handleReportClicked={handleReportClicked}
                  isPlayerView={false}
                  reportsExist={allReports.length > 0}
                />
              </div>
            )}

            {selectedReport && (
              <div className='archive-report-container'>
                <Report
                  selectedReport={selectedReport}

                  handleCloseIconClick={handleReportCloseIconClick}
                  addOrUpdateReport={updateReport}

                  reportVersionDropDownExpanded={currentReportFilterExpanded === 'reportVersion'}
                  setReportVersionDropDownExpanded={(value: boolean) => setCurrentReportFilterExpanded(value ? 'reportVersion' : undefined)}

                  playerOverview={selectedPlayerOverview}
                  playerEntry={{ id: selectedReport.playerId, name: selectedReport.playerName }}

                  isArchiveView={true}
                  isLoadingSelectedPlayerOverview={isLoadingSelectedPlayerOverview}
                />
              </div>
            )}

            {!selectedReport && allReports && reportsToShow && (
              <div className='archive-report-container' style={{ top: '1vh' }}>
                <div className='archive-report-filters-and-statistics-container'>
                  <ReportFilters
                    currentReportFilterExpanded={currentReportFilterExpanded}
                    setCurrentReportFilterExpanded={setCurrentReportFilterExpanded}
                  />

                  <div className='archive-report-filters-divider' />

                  <ReportStatistics
                    allReports={allReports}
                    reports={reportsToShow}
                  />
                </div>
              </div>
            )}
          </div>
        )}


        {activeSubTab === 'archivedTeams' && archivedTeams && (
          <div className='full-size-container'>
            <div className='archive-archived-teams-container fade-in'>
              <ExistingArchivedTeams
                archivedTeams={archivedTeams}
                selectedArchivedTeam={selectedArchivedTeam}
                handleArchivedTeamClicked={setSelectedArchivedTeam}
              />
            </div>
            {selectedArchivedTeam && (
              <div
                key={'archived-team-view-section-' + selectedArchivedTeam.id}
                className='logistic-team-view-section archived-team-view-section'
              >
                <TeamView
                  teamId={'archivedTeam'}
                  team={selectedArchivedTeam}
                  isArchivedTeamDeleted={selectedArchivedTeam['isDeleted']}
                  closeArchivedTeam={() => setSelectedArchivedTeam(undefined)}
                  deleteOrRestoreArchivedTeam={deleteOrRestoreArchivedTeam}
                  additionalPlayerOverviews={additionalPlayerOverviews}
                />
              </div>
            )}
          </div>
        )}

      </div>

    </div>
  );
};
